import Copy16 from '@carbon/icons-react/es/copy/16';
import Erase16 from '@carbon/icons-react/es/erase/16';
import OverflowMenuVertical16 from '@carbon/icons-react/es/overflow-menu--vertical/16';
import { useBoolean } from 'ahooks';
import { Dropdown, Tooltip } from 'antd';
import React, { FC, ReactNode, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { UserType } from 'api/models';
import { Button } from 'components/atoms/button';
import { Menu } from 'components/atoms/dropdown';
import { DuplicateTaskDrawer } from 'components/organisms/tasks/duplicate-task';
import { useDeleteConfirmModal } from 'hooks/use-delete-confirm-modal';
import { useRemoveAsSubtaskModal } from 'hooks/use-remove-subtask-modal';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';

interface Props {
  data: any;
  disabledAction: boolean;
  showModalAlert?: boolean;
  titleNode?: ReactNode | string;
  contentNode?: ReactNode | string;
  attachTo?: HTMLElement;
  onDelete?: (record: any) => void;
  disableDuplicateTasks?: boolean;
  disableDeleteTask: boolean;
  onRemoveAsSubtask: (record: any) => void;
}

export const SubTaskMoreActions: FC<Props> = (props) => {
  const {
    data,
    onDelete,
    disabledAction,
    attachTo,
    disableDuplicateTasks,
    disableDeleteTask,
    onRemoveAsSubtask,
  } = props;

  const translatedDeleteConfirmTitleText = useTranslation(
    'task.delete.confirm_title',
  );
  const translatedDeleteConfirmMessageText = useTranslation(
    'task.delete.confirm_message',
  );

  const translateRemoveAsSubtaskTitle = useTranslation(
    'projects.remove.as.subtask_title',
  );

  const translateRemoveAsSubtaskBody = useTranslation(
    'projects.remove.as.subtask_body',
  );

  const [isMoreActionsMenuOpen, setIsMoreActionsMenuOpen] = useState(false);
  const [
    isDuplicateTaskDrawerVisible,
    { setTrue: openDuplicateTaskDrawer, setFalse: closeDuplicateTaskDrawer },
  ] = useBoolean(false);

  const { user } = useUser();

  const handleMoreActionsMenuVisibleChange = (isVisible) => {
    setIsMoreActionsMenuOpen(isVisible);
  };

  const closeDropdown = () => {
    setIsMoreActionsMenuOpen(false);
  };

  const handleConfirmDelete = () => {
    if (onDelete) {
      onDelete(data);
    }
    closeDropdown();
  };

  const handleRemoveAsSubtask = () => {
    if (onRemoveAsSubtask) {
      onRemoveAsSubtask(data);
    }
  };

  const DeleteConfirmation = useDeleteConfirmModal({
    title: translatedDeleteConfirmTitleText,
    content: translatedDeleteConfirmMessageText,
    onOk: () => handleConfirmDelete(),
  });

  const RemoveAsSubtaskModal = useRemoveAsSubtaskModal({
    title: translateRemoveAsSubtaskTitle,
    content: translateRemoveAsSubtaskBody,
    onOk: () => handleRemoveAsSubtask(),
  });

  const menu = () => (
    <Menu>
      {user.userType === UserType.Native && !disableDuplicateTasks ? (
        <>
          <Menu.Item
            icon={<Copy16 />}
            onClick={() => {
              setIsMoreActionsMenuOpen(false);
              openDuplicateTaskDrawer();
            }}
          >
            <FormattedMessage id="task.duplicate_sub_task" />
          </Menu.Item>
          <Menu.Divider />
        </>
      ) : null}
      <Menu.Item
        icon={<Erase16 />}
        onClick={() => {
          RemoveAsSubtaskModal();
        }}
      >
        <FormattedMessage id="projects.remove.as.subtask_title" />
      </Menu.Item>
      {disableDeleteTask ? null : (
        <Menu.ItemDelete
          data-cy="sub-task-more-actions__delete"
          onClick={() => {
            DeleteConfirmation();
          }}
        />
      )}
    </Menu>
  );
  const moreActions = useTranslation('more_actions');

  return (
    <>
      {isDuplicateTaskDrawerVisible ? (
        <DuplicateTaskDrawer
          task={data}
          closeHandler={closeDuplicateTaskDrawer}
          onCancel={closeDuplicateTaskDrawer}
          fromSubTaskPane
        />
      ) : null}
      <Tooltip title={moreActions}>
        <Dropdown
          getPopupContainer={attachTo ? () => attachTo : undefined}
          disabled={disabledAction}
          visible={isMoreActionsMenuOpen}
          onVisibleChange={handleMoreActionsMenuVisibleChange}
          overlay={menu}
          trigger={['click']}
          placement="bottomRight"
        >
          <Button
            disabled={disabledAction}
            type="text"
            icon={<OverflowMenuVertical16 />}
            size="small"
          />
        </Dropdown>
      </Tooltip>
    </>
  );
};
